import React, { memo } from 'react';
import { 
  Button, 
  Chip
} from '@heroui/react';
import { Buildings, X, Copy } from '@phosphor-icons/react';
import { useMassMailContext } from '../context/MassMailContext';
import SearchComponent from 'shared/components/searchBar/components/searchComponent';
import { ResourceType } from 'shared/types/resource';
import { toast } from 'react-toastify';

export const EntitiesManager: React.FC = memo(() => {
  const { state, dispatch } = useMassMailContext();

  // Transform email search results to match SearchComponent format (from legacy modal)
  const transformEmailSearchResults = (data: any[]) => {
    if (!data || data.length === 0) {
      return [];
    }
    
    return data.map((item: any) => {
      return {
        id: item.id,
        resourceId: item.id.toString(),
        type: item.entityType,
        name: item.name,
        metadata1: item.dutchDisplayName,
        metadata2: `${Object.keys(item.fields || {}).length} velden beschikbaar`,
        entityData: item,
        fields: item.fields
      };
    });
  };

  const handleAddMentionedEntity = (resource: any) => {
    if (!resource || !resource.entityData) {
      toast.error('Entiteit data kon niet worden geladen');
      return;
    }

    const entityData = resource.entityData;
    const entityType = entityData.entityType;
    
    // Count existing entities of this type to determine the number
    const existingOfType = state.mentionedEntities.filter(e => e.type === entityType);
    const entityNumber = existingOfType.length + 1;
    const uniqueId = `${entityType}${entityNumber}`;
    
    const newEntity = {
      id: entityData.id,
      type: entityType,
      name: entityData.name,
      uniqueId: uniqueId,
      entityData: entityData,
      fields: entityData.fields || {},
      originalEntity: resource,
      dynamicFields: generateDynamicFieldsForEntity(uniqueId, entityData, entityType)
    };

    dispatch({ 
      type: 'SET_MENTIONED_ENTITIES', 
      payload: [...state.mentionedEntities, newEntity] 
    });
    
    toast.success(`${entityData.dutchDisplayName} toegevoegd: ${entityData.name}`);
  };

  const generateDynamicFieldsForEntity = (uniqueId: string, entityData: any, _entityType: string) => {
    const fields: any[] = [];
    
    if (!entityData.fields) {
      return fields;
    }

    Object.entries(entityData.fields).forEach(([fieldName, fieldData]: [string, any]) => {
      if (fieldData && fieldData.emailEnabled) {
        fields.push({
          placeholder: `{${uniqueId}.${fieldName}}`,
          label: fieldData.dutchName || fieldName,
          value: fieldData.value || '',
          type: fieldData.type || 'string'
        });
      }
    });
    
    return fields;
  };

  const removeMentionedEntity = (uniqueId: string) => {
    const updatedEntities = state.mentionedEntities.filter(entity => entity.uniqueId !== uniqueId);
    
    // Renumber entities of the same type after removal
    const renumberedEntities = renumberEntitiesByType(updatedEntities);
    
    dispatch({ type: 'SET_MENTIONED_ENTITIES', payload: renumberedEntities });
  };

  const renumberEntitiesByType = (entities: any[]) => {
    // Group entities by type
    const entityGroups: { [key: string]: any[] } = {};
    entities.forEach(entity => {
      if (!entityGroups[entity.type]) {
        entityGroups[entity.type] = [];
      }
      entityGroups[entity.type].push(entity);
    });

    // Renumber each group and regenerate dynamic fields
    const renumberedEntities: any[] = [];
    Object.keys(entityGroups).forEach(type => {
      entityGroups[type].forEach((entity, index) => {
        const newNumber = index + 1;
        const newUniqueId = `${type}${newNumber}`;
        renumberedEntities.push({
          ...entity,
          uniqueId: newUniqueId,
          dynamicFields: generateDynamicFieldsForEntity(newUniqueId, entity.entityData, type)
        });
      });
    });

    return renumberedEntities;
  };

  const copyFieldToClipboard = (placeholder: string) => {
    navigator.clipboard.writeText(placeholder);
    toast.success(`Gekopieerd: ${placeholder}`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Entiteiten</h3>
        <Button
          size="sm"
          color="primary"
          variant="flat"
          startContent={<Buildings size={16} />}
          onPress={() => dispatch({ type: 'SET_SHOW_ADD_ENTITY', payload: !state.showAddEntity })}
        >
          {state.showAddEntity ? 'Sluiten' : 'Entiteit toevoegen'}
        </Button>
      </div>

      {/* Entity Search */}
      {state.showAddEntity && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">
              Zoek naar entiteiten om te vermelden in uw email
            </p>
            <SearchComponent
              resourceFilter={[
                ResourceType.CONTACT,
                ResourceType.COMPANY,
                ResourceType.BUILDING,
                ResourceType.UNIT,
                ResourceType.HELPDESK,
                ResourceType.TASK,
                ResourceType.CONTRACT
              ]}
              onSelect={handleAddMentionedEntity}
              label="Zoek entiteit..."
              customEndpoint="/email/search-entities"
              customDataTransform={transformEmailSearchResults}
            />
          </div>
        </div>
      )}

      {/* Added Entities */}
      {state.mentionedEntities.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700">
            Toegevoegde entiteiten ({state.mentionedEntities.length})
          </h4>
          
          {state.mentionedEntities.map((entity) => (
            <div key={entity.uniqueId} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Chip size="sm" color="primary" variant="flat">
                      {entity.uniqueId}
                    </Chip>
                    <span className="font-medium text-gray-900">{entity.name}</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    {entity.fields ? Object.values(entity.fields).filter((field: any) => field.emailEnabled).length : 0} velden beschikbaar
                  </p>
                </div>
                
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isIconOnly
                  onPress={() => removeMentionedEntity(entity.uniqueId)}
                >
                  <X size={16} />
                </Button>
              </div>

              {/* Entity Fields */}
              {entity.dynamicFields && entity.dynamicFields.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {entity.dynamicFields.map((field: any, index: number) => (
                    <Chip
                      key={`${entity.uniqueId}-${index}`}
                      size="sm"
                      variant="flat"
                      color="primary"
                      className="cursor-pointer hover:bg-primary-100"
                      startContent={<Copy size={12} />}
                      onClick={() => copyFieldToClipboard(field.placeholder)}
                    >
                      {field.placeholder}
                    </Chip>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {state.mentionedEntities.length === 0 && (
        <div className="p-8 bg-gray-50 rounded-lg text-center">
          <Buildings size={32} className="mx-auto mb-2 text-gray-400" />
          <p className="text-sm text-gray-600">Geen entiteiten toegevoegd</p>
          <p className="text-xs text-gray-500 mt-1">
            Klik op "Entiteit toevoegen" om te beginnen
          </p>
        </div>
      )}
    </div>
  );
});

EntitiesManager.displayName = 'EntitiesManager';
